import { WordMatItem } from './Item';

export interface WordMatColor {
  name: string;
  bgClass: string;
  bgClassLight: string;
  borderClass: string;
  bgRgba: string;
  itemRgba: string;
}

const GALLERY_CARD_COLORS = [
  'bg-pink-100',
  'bg-green-100',
  'bg-blue-100',
  'bg-orange-100',
  'bg-violet-100',
  'bg-teal-100',
  'bg-yellow-100',
];

// Single source of truth for all word mat colors (10 entries, Tailwind-500 item colors)
export const WORD_MAT_COLORS: WordMatColor[] = [
  {
    name: 'Pink',
    bgClass: 'bg-pink-100',
    bgClassLight: 'bg-pink-50',
    borderClass: 'border-pink-300',
    bgRgba: 'rgba(252, 231, 243, 1)',
    itemRgba: 'rgba(236, 72, 153, 1)', // pink-500
  },
  {
    name: 'Green',
    bgClass: 'bg-green-100',
    bgClassLight: 'bg-green-50',
    borderClass: 'border-green-300',
    bgRgba: 'rgba(220, 252, 231, 1)',
    itemRgba: 'rgba(34, 197, 94, 1)', // green-500
  },
  {
    name: 'Blue',
    bgClass: 'bg-blue-100',
    bgClassLight: 'bg-blue-50',
    borderClass: 'border-blue-300',
    bgRgba: 'rgba(219, 234, 254, 1)',
    itemRgba: 'rgba(59, 130, 246, 1)', // blue-500
  },
  {
    name: 'Orange',
    bgClass: 'bg-orange-100',
    bgClassLight: 'bg-orange-50',
    borderClass: 'border-orange-300',
    bgRgba: 'rgba(255, 237, 213, 1)',
    itemRgba: 'rgba(249, 115, 22, 1)', // orange-500
  },
  {
    name: 'Violet',
    bgClass: 'bg-violet-100',
    bgClassLight: 'bg-violet-50',
    borderClass: 'border-violet-300',
    bgRgba: 'rgba(237, 233, 254, 1)',
    itemRgba: 'rgba(139, 92, 246, 1)', // violet-500
  },
  {
    name: 'Teal',
    bgClass: 'bg-teal-100',
    bgClassLight: 'bg-teal-50',
    borderClass: 'border-teal-300',
    bgRgba: 'rgba(204, 251, 241, 1)',
    itemRgba: 'rgba(20, 184, 166, 1)', // teal-500
  },
  {
    name: 'Yellow',
    bgClass: 'bg-yellow-100',
    bgClassLight: 'bg-yellow-50',
    borderClass: 'border-yellow-300',
    bgRgba: 'rgba(254, 249, 195, 1)',
    itemRgba: 'rgba(234, 179, 8, 1)', // yellow-500
  },
  {
    name: 'Gray',
    bgClass: 'bg-gray-100',
    bgClassLight: 'bg-gray-50',
    borderClass: 'border-gray-300',
    bgRgba: 'rgba(243, 244, 246, 1)',
    itemRgba: 'rgba(107, 114, 128, 1)', // gray-500
  },
  {
    name: 'Rose',
    bgClass: 'bg-rose-100',
    bgClassLight: 'bg-rose-50',
    borderClass: 'border-rose-300',
    bgRgba: 'rgba(255, 228, 230, 1)',
    itemRgba: 'rgba(244, 63, 94, 1)', // rose-500
  },
  {
    name: 'Slate',
    bgClass: 'bg-slate-100',
    bgClassLight: 'bg-slate-50',
    borderClass: 'border-slate-300',
    bgRgba: 'rgba(241, 245, 249, 1)',
    itemRgba: 'rgba(100, 116, 139, 1)', // slate-500
  },
];

// Maps default category names (including Spanish) to indices in WORD_MAT_COLORS
export const DEFAULT_CATEGORY_COLOR_INDEX: Record<string, number> = {
  alphabet: 9, // Slate (lowercase key used in default palette)
  Alphabet: 9, // Slate (capitalised key used in snapshot copy)
  'Consonant Digraphs': 0, // Pink
  Dígrafos: 0,
  'R-Controlled Vowels': 1, // Green
  'Silent Letters': 2, // Blue
  'Letras Mudas': 2,
  'Vowel Teams': 3, // Orange
  Diphthongs: 4, // Violet
  Diptongos: 4,
  'Double Consonants': 5, // Teal
  Endings: 6, // Yellow
  'Common Suffixes': 7, // Gray
  'Common Prefixes': 8, // Rose
};

// Derive item color from a bg class via WORD_MAT_COLORS lookup
export function getItemColorFromBgClass(bgClass: string): string {
  const entry = WORD_MAT_COLORS.find((c) => c.bgClass === bgClass);
  return entry?.itemRgba || 'rgba(107, 114, 128, 1)';
}

// Get the default item color for a category by name, with index fallback
export function getDefaultItemColor(
  category: string,
  categoryIndex?: number,
): string {
  const idx = DEFAULT_CATEGORY_COLOR_INDEX[category];
  if (idx !== undefined) {
    return WORD_MAT_COLORS[idx].itemRgba;
  }
  if (categoryIndex !== undefined) {
    return WORD_MAT_COLORS[categoryIndex % WORD_MAT_COLORS.length].itemRgba;
  }
  return 'rgba(107, 114, 128, 1)';
}

// Light background colors that complement the card colors
export function getCategoryBgColor(
  category: string,
  graphemePalette?: Record<string, WordMatItem[]>,
): string {
  // Use DEFAULT_CATEGORY_COLOR_INDEX for mapped categories
  const idx = DEFAULT_CATEGORY_COLOR_INDEX[category];
  if (idx !== undefined) {
    return WORD_MAT_COLORS[idx].bgClassLight;
  }

  // Fallback to index-based for unmapped categories
  if (graphemePalette) {
    const categoryKeys = Object.keys(graphemePalette);
    const categoryIndex = categoryKeys.indexOf(category);

    if (categoryIndex !== -1) {
      return WORD_MAT_COLORS[categoryIndex % WORD_MAT_COLORS.length]
        .bgClassLight;
    }
  }

  // Fallback for unknown categories
  return 'bg-gray-50/30';
}

export function getCardColor(id: string): string {
  let hash = 0;
  for (let i = 0; i < id.length; i++) {
    hash = id.charCodeAt(i) + ((hash << 5) - hash);
  }
  return GALLERY_CARD_COLORS[Math.abs(hash) % GALLERY_CARD_COLORS.length];
}
